import { useAuth, useOrganization, useUser } from "@clerk/clerk-react";
import { useEffect, useMemo, useState } from "react";
import LanguagePicker from "../LanguagePicker";
import { Switch } from "@headlessui/react";
import Reminder from "../ReminderToast";
import { CopyBehavior, getCopyBehaviorFromString } from "./copyBehavior";
import { updateUserMetadata } from "../../methods/clerk";
import EnumPicker from "../EnumPicker";
import { languages } from "@neum/frontend-shared/utils/LanguageUtils";
import { PhysicalExamModes, getPhysicalExamModes } from "../../utils/NotesUtils";
import { capitalize } from "lodash";
import { isDoctorRole } from "./UserRoles";
import { ClerkMetadataKeys } from "@neum/frontend-shared/constants/clerk";
import { ControlOption } from "../common/ControlOption";
import SearchableDropdown from "../common/DropdownSearchable";
import { SPECIALTIES } from "@neum/frontend-shared/constants/doctorData";
import { useVetRec } from "../../providers/VetRecProvider";
import { toTitleCase } from "@neum/frontend-shared/utils/textUtils";
import InputWithLabel from "../common/InputWithLabel";
import { CogIcon } from "@heroicons/react/24/outline";
import SyncSettingsModal from "../history/SyncSettingsModal";
import { getPimsDisplayName } from "../../utils/PimsUtils";

export const UserControlsPage = () => {
    const [language, setLanguage] = useState<string>("English (US)");
    const { user } = useUser();
    const { userSpecialty } = useVetRec();
    const { getToken, orgId, userId } = useAuth();
    const [saved, setSaved] = useState(false);
    const [savedText, setSavedText] = useState<string>("")
    const [signature, setSignature] = useState<string>("");
    const [copyBehavior, setCopyBehavior] = useState<CopyBehavior>(CopyBehavior.plain);
    const [preBuiltControlledByOrg, setPreBuiltControlledByOrg] = useState<boolean>(false);
    const [physicalExamMode, setPhysicalExamMode] = useState<PhysicalExamModes>(PhysicalExamModes.ABNORMAL_ONLY);
    const {organization, membership} = useOrganization()
    const [prebuiltEnabled, setPrebuiltEnabled] = useState<boolean>(organization?.publicMetadata?.prebuiltEnabled as boolean ?? user?.publicMetadata?.prebuiltEnabled as boolean ?? true);
    const [prebuilt_abbreviations, setPrebuilt_abbreviations] = useState<boolean>(user?.publicMetadata?.prebuilt_abbreviations as boolean ?? true);
    const { pims } = useVetRec();
    const [syncSettingsModalOpen, setSyncSettingsModalOpen] = useState(false);
    const pimsDisplayName = useMemo(() => getPimsDisplayName(pims), [pims]);
    
    const [specialty, setSpecialty] = useState<string>(() => {
        const validSpecialties = Object.values(SPECIALTIES);
        if (validSpecialties.includes(userSpecialty as typeof validSpecialties[number])) {
            return userSpecialty
        }
        return userSpecialty === "" ? SPECIALTIES.GENERAL_PRACTICE : SPECIALTIES.OTHER
    });
    const [specialtyInput, setSpecialtyInput] = useState<string>(() => {
        const validSpecialties = Object.values(SPECIALTIES);
        if (validSpecialties.includes(userSpecialty as typeof validSpecialties[number])) {
            return ""
        }
        return userSpecialty
    });
    const suggestedSignature = useMemo(() => {
        return `${membership?.role && isDoctorRole(membership.role) ? "Dr. " : ""}${user?.fullName ?? "Your Name"}`
    }, [userId]);

    const isOnPremCornerstone = pims === "on-prem-cornerstone";

    useEffect(() => {
        setLanguage(user?.publicMetadata?.language as string || "English (US)");
        setSignature(user?.publicMetadata?.custom_signature as string);
        setCopyBehavior(getCopyBehaviorFromString(user?.publicMetadata?.copyBehavior as string ?? organization?.publicMetadata?.copyBehavior ?? "plain"));
        setPrebuiltEnabled(organization?.publicMetadata?.prebuiltEnabled as boolean ?? user?.publicMetadata?.prebuiltEnabled as boolean ?? true);
        setPreBuiltControlledByOrg(organization?.publicMetadata?.prebuiltEnabled !== undefined ? true : false);
        setPhysicalExamMode(getPhysicalExamModes(user?.publicMetadata?.physical_exam_mode as string ?? organization?.publicMetadata?.physical_exam_mode as string ?? "abnormal_only"));
        setPrebuilt_abbreviations(user?.publicMetadata?.prebuilt_abbreviations as boolean ?? organization?.publicMetadata?.prebuilt_abbreviations as boolean ?? true);
    }, [user, orgId]);

    const handleCopyBehaviorChange = (value:string) => {
        setCopyBehavior(getCopyBehaviorFromString(value))
    }

    const handlePhysicalExamMode = (value:string) => {
        setPhysicalExamMode(getPhysicalExamModes(value))
    }

    const handleMedicalAbbreviationChange = (value:boolean) => {
        setPrebuilt_abbreviations(value);
    }

    const handlePrebuiltChange = (value:boolean) => {
        setPrebuiltEnabled(value);
    };

    const handleSpecialtyChange = (value:string| undefined) => {
        setSpecialty(value ?? "");
    }

    const handleSaveControls = async () => {
        setSavedText("Saving settings...");
        setSaved(true);
        await updateUserMetadata(
            { 
                [ClerkMetadataKeys.PREBUILT_ENABLED]: prebuiltEnabled,
                [ClerkMetadataKeys.COPY_BEHAVIOR]: copyBehavior,
                [ClerkMetadataKeys.LANGUAGE]: language,
                [ClerkMetadataKeys.CUSTOM_SIGNATURE]: signature,
                [ClerkMetadataKeys.PHYSICAL_EXAM_MODE]: physicalExamMode,
                [ClerkMetadataKeys.PREBUILT_ABBREVIATIONS]: prebuilt_abbreviations,
                [ClerkMetadataKeys.SPECIALTY]: specialty === SPECIALTIES.OTHER ? specialtyInput : specialty,
            },
            "update_user_controls",
            (await getToken({ template: "supabase" })) ?? ""
        );
        await user?.reload()
    }


    return (<>
        <div className="mb-20 flex flex-col gap-y-4 w-full">
            <div className="flex flex-row justify-between mt-4">
                <div className="flex flex-col gap-y-4">
                    <h1 className="text-3xl font-semibold">User Controls</h1>
                    <div>Configure your user options.</div>
                </div>
                <div>
                    <button className="bg-blue-600 px-4 py-2 text-white rounded-md" onClick={() => handleSaveControls()}>Save</button>
                </div>
            </div>
            <div className="text-xl font-semibold mt-4">
                Note Configurations
            </div>
            <ControlOption
                label="Signature"
                description="Tell VetRec how you would like your name displayed in documents (e.g., downloaded notes)."
                control={
                    <input
                        type="text"
                        placeholder={suggestedSignature}
                        className="border border-gray-300 rounded-md p-2 w-60 text-sm"
                        value={signature}
                        onChange={(e) => setSignature(e.target.value)}
                    />
                }
            />
            <ControlOption
                label="Copy Behavior"
                description={"Choose between copying notes as plain text or **markdown**"}
                control={
                    <EnumPicker 
                        enum={CopyBehavior} 
                        selectedOption={copyBehavior} 
                        setSelectedOption={(value) => handleCopyBehaviorChange(value)} 
                        width="w-60"
                    />
                }
            />
            <ControlOption
                label="Default Language"
                description="Default language used to transcribe and generate notes."
                control={
                    <LanguagePicker 
                        language={language} 
                        setLanguage={setLanguage} 
                        className="" 
                        labelBackground="bg-white" 
                        languages={languages}
                    />
                }
            />
            <ControlOption
                label="Physical Exam Behavior"
                description="Do you want the physical exam to only document abnormals and use default values for normals or would you want it to document everything you say verbatim?"
                control={
                    <EnumPicker 
                        enum={PhysicalExamModes} 
                        selectedOption={physicalExamMode} 
                        setSelectedOption={(value) => handlePhysicalExamMode(value)} 
                        width="w-60" 
                        enumToDisplayName={(value) => capitalize(value.replace(/_/g, " "))}
                    />
                }
            />
            <ControlOption
                label="Pre-built Abbreviations"
                description="Enable the use of pre-built abbreviations in notes (e.g., OD for Right Eye, OS for Left Eye). Disable to display full terms instead."
                control={
                    <Switch
                    checked={prebuilt_abbreviations}
                    onChange={handleMedicalAbbreviationChange}
                    className={`${
                        prebuilt_abbreviations ? 'bg-blue-600' : 'bg-gray-200'
                    } relative inline-flex h-6 w-11 items-center rounded-full flex-shrink-0`}
                >
                    <span className="sr-only">Are pre-built templates enabled?</span>
                    <span
                        className={`${
                            prebuilt_abbreviations ? 'translate-x-6' : 'translate-x-1'
                        } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                    />
                </Switch>
                }
            />
            <div className="text-xl font-semibold mt-4">
                Template Configurations
            </div>
            <ControlOption
                label="Specialty"
                description="Configure your specialty. This will be used to determine which pre-built templates are shown to you."
                control={
                    <div className="flex flex-row gap-x-2">
                        {specialty === SPECIALTIES.OTHER && <InputWithLabel
                            value={specialtyInput}
                            setValue={(value) =>
                                setSpecialtyInput(value as string)
                            }
                            type="text"
                            label="custom input"
                            placeholder="General Practice, Neurology, etc."
                            maxLength={80}
                            inputClassName="h-[34px] sm:h-[34px]"
                        />}
                        <SearchableDropdown<string>
                            items={Object.values(SPECIALTIES)}
                            selectedItem={specialty}
                            setSelectedItem={(specialty) =>
                                handleSpecialtyChange(specialty)
                            }
                            displayValue={(item) => (item ? toTitleCase(item) : "")}
                            filterFunction={(item, query) =>
                            item.toLowerCase().includes(query.toLowerCase())
                            }
                            placeholder="General Practice, Neurology, etc."
                        />
                  </div>
                }
            />
            <ControlOption
                label="Pre-built Templates"
                description="Configure whether pre-built templates are enabled for your account. If disabled, pre-built templates won't be shown unless added."
                control={
                    <Switch
                        checked={prebuiltEnabled}
                        onChange={handlePrebuiltChange}
                        disabled={preBuiltControlledByOrg}
                        className={`${
                            prebuiltEnabled ? 'bg-blue-600' : 'bg-gray-200'
                        } relative inline-flex h-6 w-11 items-center rounded-full flex-shrink-0`}
                    >
                        <span className="sr-only">Are pre-built templates enabled?</span>
                        <span
                            className={`${
                                prebuiltEnabled ? 'translate-x-6' : 'translate-x-1'
                            } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                        />
                    </Switch>
                }
            />
            
            {/* Cornerstone Settings */}
            {isOnPremCornerstone && (
                <>
                    <div className="text-xl font-semibold mt-4">
                        {pimsDisplayName} Configurations
                    </div>
                    <ControlOption
                        label="Appointment Type Sync Settings"
                        description={`Configure which appointment types to sync from ${pimsDisplayName}. These settings are used when syncing appointments from the History page.`}
                        control={
                            <button
                                onClick={() => setSyncSettingsModalOpen(true)}
                                className="flex items-center gap-x-2 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                                <CogIcon className="h-4 w-4" />
                                Configure Sync Settings
                            </button>
                        }
                    />
                </>
            )}
        </div>
        
        {/* Sync Settings Modal */}
        {isOnPremCornerstone && (
            <SyncSettingsModal
                isOpen={syncSettingsModalOpen}
                onClose={() => setSyncSettingsModalOpen(false)}
                pimsDisplayName={pimsDisplayName}
            />
        )}
    </>);
};